/**
 * The AI Service detail page's Developer Info tab (task 811).
 *
 * **The one page a builder sends their developer.** Everything to the left of
 * this tab in the row belongs to the person who owns the service; this tab
 * belongs to the person who integrates it — which is why it sits apart at the
 * far right and why nothing on it edits anything. Building, configuring,
 * publishing and promoting all stay in their home tabs; Developer Info describes
 * the result and links back to whichever tab owns the change.
 *
 * What it collected. Before this tab, integration material was scattered by
 * adjacency to other features: the call URL, key minting and snippets sat at the
 * bottom of **Releases** underneath version lanes the developer does not manage;
 * the schemas were only readable inside the schema-card editor; and MCP
 * exposure — the first thing an AI-tool-using developer needs — was shown
 * nowhere on the page at all. The call section moved here whole, so Releases is
 * now purely the shipping story.
 *
 * **A rail, not a long scroll** (kv's note on the report). The four sections are
 * each long enough to bury the others — the OpenAPI document most of all — which
 * is the shape `SectionRail` exists for: the same rail idiom the Agents dossier
 * wears, so the two read as one design. API is the default: it is what the
 * majority of integrations need and the only section that is never empty.
 *
 * The handoff header carries a **Copy link** button for this tab's own
 * `?tab=developer` address. That address is the deliverable — the builder pastes
 * one URL and the developer lands exactly here.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowTopRightOnSquareIcon,
  CheckIcon,
  ClipboardDocumentIcon,
  CodeBracketIcon,
  CpuChipIcon,
  DocumentTextIcon,
  LinkIcon,
  WindowIcon,
} from '@heroicons/react/24/outline';
import type { Endpoint } from '@/types';
import { useToast } from '@/contexts/ToastContext';
import { useActiveCustomDomainHostnames } from '@/hooks/useCustomDomains';
import { apiServerUrl, customDomainUrl, endpointUiUrl } from '@/utils/endpointUrl';
import { jsonSchemaToFields, type FieldDefinition } from '@/utils/schemaBuilder';
import { ApiDocsPanel } from '@/components/endpoints/ApiDocsPanel';
import { EnvironmentUrlList } from '@/components/environments/EnvironmentUrlList';
import { ConnectExplainer } from '@/components/shared/ConceptExplainers';
import SeeItLive, { demoForAuthMode } from '@/components/shared/SeeItLive';
import PreviewSandbox from '@/components/ui-composer/PreviewSandbox';
import { SectionRail, type SectionRailEntry } from './SectionRail';
import { ServiceCallSection } from './ServiceCallSection';
import { useServiceInterfaceVersions } from './serviceInterfaceVersions';
import { mcpClientConfig, mcpToolName } from './serviceSnippets';

/** The four questions this tab answers, in the order a developer asks them. */
type DeveloperSection = 'api' | 'api-docs' | 'mcp' | 'ui';

/**
 * API Docs sits SECOND, directly after the section it documents (task 821).
 *
 * The alternative — docs last, after MCP and UI — would separate the OpenAPI
 * contract from the call snippets generated out of the same schemas, and a
 * developer who has just read "here is the call" asks "what else does it accept"
 * next, not "how do I add this to an AI client". MCP and UI are alternative ways
 * to consume the service, so they stay after both API surfaces.
 */
const SECTION_ENTRIES: SectionRailEntry[] = [
  { id: 'api', label: 'API', summary: 'Call the service', icon: CodeBracketIcon },
  { id: 'api-docs', label: 'API Docs', summary: 'Interactive OpenAPI docs', icon: DocumentTextIcon },
  { id: 'mcp', label: 'MCP', summary: 'AI tool config', icon: CpuChipIcon },
  { id: 'ui', label: 'UI', summary: 'Hosted interface', icon: WindowIcon },
];

const cardClasses =
  'rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800';

/**
 * A cross-tab pointer: "the thing you are reading about is CHANGED over there."
 *
 * A relative `?tab=` link rather than a click handler, so it is a real
 * navigable link — the page already drives tab selection off the query string,
 * so setting the param is the whole mechanism.
 */
function TabPointer({
  tab,
  children,
  testId,
}: {
  tab: string;
  children: ReactNode;
  testId: string;
}) {
  return (
    <Link
      to={`?tab=${tab}`}
      data-testid={testId}
      className="inline-flex items-center text-xs font-semibold text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
    >
      {children}
    </Link>
  );
}

/** Dark code block with a copy button, matching `ServiceCallSection`'s. */
function CodeBlock({
  code,
  onCopy,
  testId,
}: {
  code: string;
  onCopy: (code: string) => void;
  testId: string;
}) {
  return (
    <div className="relative mt-3">
      <button
        type="button"
        onClick={() => onCopy(code)}
        className="absolute right-2 top-2 rounded-md bg-gray-700 px-2.5 py-1 text-xs font-medium text-gray-200 hover:bg-gray-600"
      >
        Copy
      </button>
      <pre
        data-testid={testId}
        className="overflow-x-auto rounded-md bg-gray-900 p-4 font-mono text-xs leading-relaxed text-gray-100 dark:bg-gray-950"
      >
        <code>{code}</code>
      </pre>
    </div>
  );
}

/** One schema's fields, names and types only — the contract at a glance. */
function SchemaSummary({
  title,
  fields,
  emptyText,
  testId,
}: {
  title: string;
  fields: FieldDefinition[];
  emptyText: string;
  testId: string;
}) {
  return (
    <div data-testid={testId}>
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
        {title}
      </p>
      {fields.length === 0 ? (
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{emptyText}</p>
      ) : (
        <ul className="mt-2 space-y-1">
          {fields.map((field) => (
            <li
              key={field.name}
              data-testid="schema-summary-field"
              className="flex items-baseline gap-2 text-xs"
            >
              <span className="font-mono font-medium text-gray-900 dark:text-gray-100">
                {field.name}
              </span>
              <span className="font-mono text-gray-500 dark:text-gray-400">{field.type}</span>
              {field.required && (
                <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                  required
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * API — how a system calls this service.
 *
 * The environment URL table leads, because *which* URL to call is the question
 * that precedes how to call it, and the answer depends on which stage the
 * developer is wiring. `EnvironmentUrlList` is the same component the Releases
 * surface and the API Docs tab mount (task 799) rather than a second URL table:
 * it composes every URL through `utils/endpointUrl`, and it renders nothing at
 * all for an account with no environments — which is exactly the
 * "non-managed service shows the single bare URL only" case, since the bare URL
 * is the one `ServiceCallSection` carries below it.
 */
function DeveloperApiSection({ endpoint }: { endpoint: Endpoint }) {
  const inputFields = useMemo(() => jsonSchemaToFields(endpoint.input ?? null), [endpoint.input]);
  const outputFields = useMemo(() => jsonSchemaToFields(endpoint.output ?? null), [endpoint.output]);

  // The demo matching how this service is called (task 540): public → the
  // browser widget site, apiKey → the server-side shop. Other modes show none.
  const callDemo = demoForAuthMode(endpoint.authPolicy?.mode);

  return (
    <div className="space-y-6" data-testid="developer-api-section">
      <ConnectExplainer />
      <EnvironmentUrlList endpoint={endpoint} />
      <ServiceCallSection endpoint={endpoint} />
      {callDemo && <SeeItLive example={callDemo} />}

      <section className={`${cardClasses} p-4`} data-testid="developer-schema-summary">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              Request and response fields
            </h3>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              The contract the snippets above are generated from.
            </p>
          </div>
          {/* Overview owns the editable schema cards since task 882 retired the
              Input/Output tab. The pointer names the tab that takes the change,
              so it moved with the cards rather than leaning on the alias — a
              handoff link should not redirect. This is a summary; editing is
              there. */}
          <TabPointer tab="overview" testId="developer-schema-edit-link">
            Edit on Overview →
          </TabPointer>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2">
          <SchemaSummary
            title="Request"
            fields={inputFields}
            emptyText="No input schema — this service accepts any JSON."
            testId="developer-schema-input"
          />
          <SchemaSummary
            title="Response"
            fields={outputFields}
            emptyText="No output schema — this service returns any JSON."
            testId="developer-schema-output"
          />
        </div>
      </section>
    </div>
  );
}

/**
 * API Docs — the same OpenAPI contract the Advanced page's API Docs tab shows,
 * mounted here so a builder's developer never needs the Advanced page (task 821).
 *
 * The whole surface comes from `ApiDocsPanel`: the client-side projection, the
 * Swagger renderer, the raw JSON with copy and download. What this section adds
 * is only where it sits and what it says it is for.
 *
 * **The server dropdown is what kv asked for.** `ApiDocsPanel` reads the
 * account's environments and hands them to `endpointToOpenApiDocument`, which
 * emits one `servers` entry per stage — plain URL first, labelled by what it
 * actually serves, then each environment in promotion order (task 799). Swagger
 * turns that array into the picker a developer chooses a stage from, and
 * Try it out then calls exactly that stage.
 *
 * No `EnvironmentUrlList` here: the API section one rail click away already
 * shows it, and the same table twice on one tab is noise.
 */
function DeveloperApiDocsSection({ endpoint }: { endpoint: Endpoint }) {
  return (
    <div className="space-y-4" data-testid="developer-api-docs-section">
      <ApiDocsPanel
        endpoint={endpoint}
        subtitle="This AI Service as an OpenAPI 3.1 contract. Pick an environment from the server dropdown to try a call against that stage."
        testId="developer-api-docs-panel"
      />
    </div>
  );
}

/**
 * MCP — this service as a tool an AI client can call.
 *
 * The server is ACCOUNT-level: one `/api/mcp/endpoints` Streamable HTTP server
 * exposing every service in the account whose `mcpExposed` is on. So the
 * question this section answers is not "what is this service's MCP URL" but
 * "is this service in that catalog, and under what name".
 *
 * No toggle. Exposure is a property of the service, changed on Overview, and
 * offering the switch here would make a read-only tab the second place a
 * service can be reconfigured. (Task 850 put the switch on Overview beside the
 * other call-shape properties; until then this pointed at Config, where no such
 * control has ever existed.)
 *
 * **On a custom domain the address is a DIFFERENT SERVER, not the same one
 * renamed** (task 857). The account catalog lives at `/api/mcp/endpoints`, which
 * carries no slug — so the edge's one rule (public = internal minus
 * `/api/{slug}`) cannot produce it, and it has no custom-domain form at all.
 * What a custom domain does serve is this service's OWN MCP server, at
 * `https://{hostname}/{path}/mcp`. Both are offered, with the domain's leading,
 * and each says what it carries: one service, or every MCP-exposed service in
 * the account. Presenting them as one address in two spellings would be a lie
 * about the tool list a client would see.
 */
function DeveloperMcpSection({
  endpoint,
  onCopy,
}: {
  endpoint: Endpoint;
  onCopy: (text: string, label: string) => void;
}) {
  const accountServerUrl = `${apiServerUrl()}/mcp/endpoints`;
  const customDomains = useActiveCustomDomainHostnames();
  const primaryHostname = customDomains[0] ?? null;
  const toolName = mcpToolName(endpoint);
  const serverUrl = primaryHostname
    ? customDomainUrl(endpoint, primaryHostname, { surface: 'mcp' })
    : accountServerUrl;
  const clientConfig = useMemo(
    () =>
      mcpClientConfig(
        endpoint,
        serverUrl,
        // A per-service server needs a per-service key, or a developer pasting
        // two of these overwrites the first entry with the second.
        primaryHostname
          ? `${endpoint.accountSlug ? `${endpoint.accountSlug}-` : ''}${toolName.replace(
              /[^a-zA-Z0-9-]+/g,
              '-'
            )}`
          : undefined
      ),
    [endpoint, primaryHostname, serverUrl, toolName]
  );

  if (!endpoint.mcpExposed) {
    return (
      <section
        className={`${cardClasses} p-6`}
        data-testid="developer-mcp-section"
        data-exposed="false"
      >
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">MCP</h3>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          This service is not exposed as an MCP tool, so it does not appear in the account&rsquo;s
          MCP tool catalog.
        </p>
        <p className="mt-3">
          <TabPointer tab="overview" testId="developer-mcp-config-link">
            The MCP exposure setting is on the Overview tab →
          </TabPointer>
        </p>
      </section>
    );
  }

  return (
    <section
      className={`${cardClasses} p-6`}
      data-testid="developer-mcp-section"
      data-exposed="true"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="h-2 w-2 rounded-full bg-green-500" aria-hidden="true" />
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          Exposed as an MCP tool
        </h3>
        <code
          data-testid="developer-mcp-tool-name"
          className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-800 dark:bg-gray-700 dark:text-gray-200"
        >
          {toolName}
        </code>
      </div>

      <dl className="mt-4 space-y-1">
        <dt className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
          Server
        </dt>
        <dd className="flex flex-wrap items-center gap-2">
          <span
            data-testid="developer-mcp-server-url"
            className="break-all font-mono text-sm text-gray-900 dark:text-gray-100"
          >
            {serverUrl}
          </span>
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
            Streamable HTTP
          </span>
          {primaryHostname && (
            <span
              data-testid="developer-mcp-custom-domain-badge"
              className="rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-medium text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200"
            >
              On your custom domain
            </span>
          )}
          <button
            type="button"
            onClick={() => onCopy(serverUrl, 'MCP server URL')}
            className="inline-flex items-center text-xs font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
          >
            <ClipboardDocumentIcon className="mr-1 h-4 w-4" />
            Copy
          </button>
        </dd>
        {primaryHostname && (
          <dd
            data-testid="developer-mcp-account-server-url"
            className="flex flex-wrap items-center gap-2 pt-1 text-xs text-gray-500 dark:text-gray-400"
          >
            <span className="font-medium">Platform URL (always available)</span>
            <span className="min-w-0 break-all font-mono">{accountServerUrl}</span>
            <button
              type="button"
              onClick={() => onCopy(accountServerUrl, 'Account MCP server URL')}
              aria-label="Copy the account MCP server URL"
              className="inline-flex shrink-0 items-center font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
            >
              <ClipboardDocumentIcon className="mr-1 h-3.5 w-3.5" />
              Copy
            </button>
          </dd>
        )}
      </dl>

      <p className="mt-5 text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
        Client configuration
      </p>
      <CodeBlock
        code={clientConfig}
        onCopy={(code) => onCopy(code, 'MCP client config')}
        testId="developer-mcp-client-config"
      />
      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
        {primaryHostname ? (
          <>
            This entry connects to <span className="font-medium">this AI Service alone</span>. The
            platform URL above is the account&rsquo;s whole MCP catalog — every MCP-exposed service
            in one entry — if that is what the client should see instead.
          </>
        ) : (
          <>The server carries every MCP-exposed service in this account, so one entry is enough.</>
        )}{' '}
        Mint an API key in the API section and paste it in place of{' '}
        <code className="font-mono">&lt;YOUR_API_KEY&gt;</code>.
      </p>
    </section>
  );
}

/**
 * UI — the generated interface, from the integrator's angle.
 *
 * **The interface itself, not a link to it** (task 818, kv's screenshot review).
 * A developer opening this section is asking "what is this interface", and the
 * honest answer is to show it: the same `PreviewSandbox` the Interface tab
 * renders, on the same version, resolved through the same hook so the two can
 * never disagree about which version that is.
 *
 * Read-only in a way the Interface tab is not. No version rail, no Hide/Show, no
 * route into the Composer — this section describes the interface a developer has
 * to integrate against, and every affordance for changing it stays one link away
 * on the tab that owns it. That is also why the empty state's call to action
 * points at the Interface tab rather than the Composer flow the Interface tab's
 * own empty state opens.
 *
 * The hosted URL is the app's own interface address (the same link
 * `ServiceInterfaceSection`'s Copy link produces), because that is where the
 * interface is actually served: the platform has no standalone public UI route,
 * and printing an invented `/ui` address would hand the developer a 404.
 */
function DeveloperUiSection({
  endpoint,
  onCopy,
}: {
  endpoint: Endpoint;
  onCopy: (text: string, label: string) => void;
}) {
  const hostedUrl = `${window.location.origin}/services/${endpoint.id}?tab=interface`;
  const { current, isLoading } = useServiceInterfaceVersions(endpoint.id);

  /**
   * THE PUBLIC PAGE, WHEN THERE IS ONE (task 857).
   *
   * The link above is the in-app page, which anyone opening it signs in for; it
   * has no custom-domain form because it is not served by the edge at all. The
   * address a developer integrates against is the PUBLIC one — `/ui`, one
   * segment past the service — which exists only while the interface is exposed
   * AND a version is pinned, the same two gates the Interface tab states. So it
   * is shown only then, custom domain first, and nothing changes here for a
   * service whose interface is not published.
   */
  const customDomains = useActiveCustomDomainHostnames();
  const primaryHostname = customDomains[0] ?? null;
  const publicPageLive = Boolean(endpoint.uiExposed && endpoint.publishedUiId);
  const publicUrl = endpointUiUrl(endpoint, primaryHostname);
  const platformPublicUrl = endpointUiUrl(endpoint);

  return (
    <div className="space-y-6" data-testid="developer-ui-section">
      <section className={`${cardClasses} overflow-hidden`}>
        <div className="border-b border-gray-100 px-4 py-3 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Hosted interface</h3>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            Innovate serves this service&rsquo;s generated interface here. Anyone opening it signs
            in to the account first.
          </p>
        </div>

        <div className="p-4">
          {publicPageLive && (
            <div className="mb-4" data-testid="developer-ui-public-page">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                Public page
                {primaryHostname && (
                  <span
                    data-testid="developer-ui-custom-domain-badge"
                    className="ml-2 inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-medium normal-case tracking-normal text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200"
                  >
                    On your custom domain
                  </span>
                )}
              </p>
              <div className="mt-1 flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 dark:border-gray-600 dark:bg-gray-900">
                <span
                  data-testid="developer-ui-public-url"
                  className="min-w-0 flex-1 break-all font-mono text-sm text-gray-900 dark:text-gray-100"
                >
                  {publicUrl}
                </span>
                <button
                  type="button"
                  onClick={() => onCopy(publicUrl, 'Public page URL')}
                  className="inline-flex shrink-0 items-center text-sm font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
                >
                  <ClipboardDocumentIcon className="mr-1 h-4 w-4" />
                  Copy
                </button>
                <a
                  href={publicUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="developer-ui-public-open"
                  className="inline-flex shrink-0 items-center text-sm font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
                >
                  Open
                  <ArrowTopRightOnSquareIcon className="ml-1 h-4 w-4" />
                </a>
              </div>
              {primaryHostname && (
                <p
                  data-testid="developer-ui-public-url-platform"
                  className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400"
                >
                  <span className="font-medium">Platform URL (always available)</span>
                  <span className="min-w-0 break-all font-mono">{platformPublicUrl}</span>
                  <button
                    type="button"
                    onClick={() => onCopy(platformPublicUrl, 'Platform page URL')}
                    aria-label="Copy the platform page URL"
                    className="inline-flex shrink-0 items-center font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
                  >
                    <ClipboardDocumentIcon className="mr-1 h-3.5 w-3.5" />
                    Copy
                  </button>
                </p>
              )}
              <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                In this app
              </p>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 dark:border-gray-600 dark:bg-gray-900">
            <span
              data-testid="developer-ui-url"
              className="min-w-0 flex-1 break-all font-mono text-sm text-gray-900 dark:text-gray-100"
            >
              {hostedUrl}
            </span>
            <button
              type="button"
              onClick={() => onCopy(hostedUrl, 'Interface URL')}
              className="inline-flex shrink-0 items-center text-sm font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
            >
              <ClipboardDocumentIcon className="mr-1 h-4 w-4" />
              Copy
            </button>
            <a
              href={hostedUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="developer-ui-open"
              className="inline-flex shrink-0 items-center text-sm font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
            >
              Open
              <ArrowTopRightOnSquareIcon className="ml-1 h-4 w-4" />
            </a>
          </div>

          <div className="mt-4">
            {isLoading ? (
              <p
                data-testid="developer-ui-preview-loading"
                className="py-8 text-center text-sm text-gray-500 dark:text-gray-400"
              >
                Loading interface…
              </p>
            ) : current ? (
              <>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                  What it looks like
                </p>
                {/* The same hardened frame the Interface tab uses — imported,
                    never re-implemented: its isolation (scripts, but no
                    same-origin) is the property that makes rendering generated
                    source safe anywhere in the app. */}
                <div data-testid="developer-ui-preview">
                  <PreviewSandbox source={current.source} endpoint={endpoint} />
                </div>
              </>
            ) : (
              <div
                data-testid="developer-ui-empty"
                className="rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-8 text-center dark:border-gray-600 dark:bg-gray-800/50"
              >
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  This service has no interface yet
                </p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  There is nothing to embed or link to until one is generated.
                </p>
                <p className="mt-3">
                  {/* The Interface tab, not the Composer: creating an interface
                      is the owner's job, and this section is read by someone
                      who may not have that seat. */}
                  <TabPointer tab="interface" testId="developer-ui-empty-link">
                    Create one on the Interface tab →
                  </TabPointer>
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      <p>
        <TabPointer tab="interface" testId="developer-ui-interface-link">
          Building or changing the interface happens on the Interface tab →
        </TabPointer>
      </p>
    </div>
  );
}

interface ServiceDeveloperTabProps {
  endpoint: Endpoint;
  /** Display name for the handoff line — the same title the page header shows. */
  resourceName?: string;
}

export function ServiceDeveloperTab({ endpoint, resourceName }: ServiceDeveloperTabProps) {
  const toast = useToast();
  const [section, setSection] = useState<DeveloperSection>('api');
  const [linkCopied, setLinkCopied] = useState(false);

  const handleCopy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard`);
    } catch {
      toast.error('Failed to copy');
    }
  };

  /**
   * This tab's own address. Read off `window.location` rather than rebuilt from
   * the route, so what is copied is what the reader is looking at — including
   * the deployed origin, which is the half that makes it shareable.
   */
  const handleCopyLink = async () => {
    const link = `${window.location.origin}${window.location.pathname}?tab=developer`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Developer Info link copied', link);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 1500);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  return (
    <div className="space-y-5" data-testid="service-developer-tab">
      {/* Handoff header: what this page is FOR, and the one URL that is the
          handoff itself. */}
      <div
        className={`${cardClasses} flex flex-wrap items-start justify-between gap-3 p-4`}
        data-testid="developer-handoff-header"
      >
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900 dark:text-white">
            Everything needed to integrate {resourceName ?? 'this AI Service'}.
          </p>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            Send this page to your developer — nothing here changes the service.
          </p>
        </div>
        <button
          type="button"
          onClick={handleCopyLink}
          data-testid="developer-copy-link"
          className="inline-flex shrink-0 items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
        >
          {linkCopied ? (
            <CheckIcon className="-ml-0.5 mr-1.5 h-4 w-4 text-green-500" />
          ) : (
            <LinkIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
          )}
          Copy link
        </button>
      </div>

      <div className="flex flex-col gap-5 md:flex-row md:items-start">
        {/* Below md the rail stacks above the section. At md and up it holds its
            width and sticks, the same behaviour the Agents rail has. */}
        <div className="md:sticky md:top-4 md:w-[260px] md:shrink-0">
          <SectionRail
            heading="Developer info"
            entries={SECTION_ENTRIES}
            selectedId={section}
            onSelect={(id) => setSection(id as DeveloperSection)}
            testId="developer-rail"
            ariaLabel="Developer info sections"
          />
        </div>

        <div className="min-w-0 flex-1">
          {section === 'api' && <DeveloperApiSection endpoint={endpoint} />}
          {section === 'api-docs' && <DeveloperApiDocsSection endpoint={endpoint} />}
          {section === 'mcp' && <DeveloperMcpSection endpoint={endpoint} onCopy={handleCopy} />}
          {section === 'ui' && <DeveloperUiSection endpoint={endpoint} onCopy={handleCopy} />}
        </div>
      </div>
    </div>
  );
}

export default ServiceDeveloperTab;
